import React from 'react';
import { identity, noop } from 'lodash';

import MoneyInput from 'components/form/MoneyInput.component';
import Space from 'components/generic/Space.component';
import CurrencySign from 'components/price/CurrencySign.component';
import S from 'constants/string.const';
import { useTranslation, withTranslation } from 'providers/GILT.provider';
import alen from 'utils/arrays/alen.util';
import { replaceAt } from 'utils/arrays/array.util';
import asArray from 'utils/arrays/asArray.util';
import createFullArray from 'utils/arrays/createFullArray.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToNumber from 'utils/converters/fromAnyToNumber.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import { fromAnyToJsonClone, fromAnyToUglyJson as J } from 'utils/converters/json.util';
import fromArrayToCommatose from 'utils/converters/string/fromArrayToCommatose.util';
import moveCursorAtEnd from 'utils/dom/input/moveCursorAtEnd.util';
import logger from 'utils/logger.util';
import parseMoney from 'utils/parsers/parseMoney.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import isFalsy from 'utils/predicates/isFalsy.util';
import createNodeKey from 'utils/react/createNodeKey.util';
import { concatTag as ct } from 'utils/strings/manipulate.util';

import UnlockedDeposit from 'checkout/components/deposit/ItemContainer';

const L = logger('ClientsDepositTable');

const USE_LOADER = false;
const USE_OLD_USE_DEPOSIT = false;
const USE_ALWAYS_SHOW_DETAILS = false;
const USE_NEW_UNTESTED_DEPOSIT_UNLOCK = false;

const DEPOSIT_TEXT_TRANSLATION_KEYS = Object.freeze({
  card: 'checkout.clientsDepositTable.card',
  cash: 'checkout.clientsDepositTable.cash',
  online: 'checkout.clientsDepositTable.online',
  slick_pos: 'checkout.clientsDepositTable.slickPayReader',
  other: 'checkout.clientsDepositTable.other',
});

const STYLE_ERROR = Object.freeze({
  color: '#D32F2F',
  fontSize: '11px',
  textAlign: 'left',
  display: 'block',
  paddingLeft: '8px',
});

const STYLE_NO_PADDING = Object.freeze({ padding: '0' });

const calculateSums = (deposits) => {
  let availableSum = 0;
  let lockedSum = 0;
  let usedSum = 0;

  // TODO: replace for() with .reduce()
  for (const { available, locked, use_deposit: used } of asArray(deposits)) {
    lockedSum += locked;
    availableSum += available;
    if (used > 0) usedSum += used;
  } // for

  return { used: usedSum, available: availableSum, locked: lockedSum };
};

const LoadingIndicator = () => {
  const { t } = useTranslation();
  return (
    <div data-bem="ClientDepositTable_loading-indicator" className="tr" style={STYLE_NO_PADDING}>
      <div className="tr loading-in-tr">
        <div className="spinner-container">
          <div className="load-spinner" />
          <span className="spinner-text">{t('checkout.clientsDepositTable.loadingIndicator')}</span>
        </div>
      </div>
    </div>
  );
};

const ClientDepositLine = ({
  tag,

  available,
  checked,
  clientName,
  errorFeId,
  errorInputMsg,
  feId,
  id,

  onMoneyBlur,
  onChange,
  onMoneyChange,
  onViewDetails,
  onMoneyClick,

  source,
  locked,
  redeemed,
  t,
}) => (
  <div data-bem="ClientDepositLine" data-tag={tag} className="tr">
    <div data-bem="ClientDepositLine__cell-client" className="cell-name checkbox-td">
      <div className="checkbox-box-custom-wrap">
        <input data-bem="ClientDepositLine__value-checkbox" type="checkbox" className="checkbox-box-custom" id={id} checked={checked} onChange={onChange} disabled={available === 0} />

        <label data-bem="ClientDepositLine__client-name" className="checkbox-box-label" htmlFor={id}>
          <div className="checkbox-box-custom-icon">
            <span className="ic-sml-18px ic_check_white_18" />
          </div>
          <span data-bem="ClientDepositLine__value-client-name" className="text-sb client-name text-cap">
            {clientName}
          </span>
        </label>

        <input type="checkbox" className="checkbox-switch" />

        <span className={available === 0 ? 'tooltip' : 'no_tooltip'}>
          <div className="tooltip-centered">{t('checkout.clientsDepositTable.tooltipText')}</div>
        </span>
      </div>
    </div>

    <div data-bem="ClientDepositLine__cell-available-deposit" className="cell-balance text-r">
      <span className="text-gray">
        {t(DEPOSIT_TEXT_TRANSLATION_KEYS[source]) || t('checkout.clientsDepositTable.unknownSource')} {t('checkout.clientsDepositTable.deposit')}
      </span>
      <span data-bem="ClientDepositLine__value-available-deposit" className="text-sb">
        <CurrencySign value={available} />
      </span>
    </div>

    <div data-bem="ClientDepositLine__cell-other-deposit" className="cell-use text-r">
      <span className="text-gray">{t('checkout.clientsDepositTable.unavailable')}</span>
      <span data-bem="ClientDepositLine__value-other-deposit" className="text-sb">
        <CurrencySign value={locked} />
      </span>
      <div className="deposit-details">
        {(USE_ALWAYS_SHOW_DETAILS || locked !== 0) && (
          <button onClick={onViewDetails} className="btn btn-violet">
            {t('checkout.clientsDepositTable.viewDetailsButton')}
          </button>
        )}
      </div>
    </div>

    <div data-bem="ClientDepositLine__cell-redeemed" className="cell-used text-r">
      <span className="text-gray">
        <Space>{t('checkout.clientsDepositTable.use')}</Space>
      </span>

      <span className="text-sb">
        <Space>
          <CurrencySign />
        </Space>
      </span>

      <MoneyInput
        tag="ClientDepositLine__value-redeemed"
        type="text"
        autoComplete="off"
        placeholder=""
        disabled={available === 0}
        value={redeemed}
        onClick={onMoneyClick}
        onChange={onMoneyChange}
        onBlur={onMoneyBlur}
      />
    </div>

    <div data-bem="ClientDepositLine__error" className="cell-empty">
      {fromAnyToBool(errorFeId === feId && errorInputMsg) && <span style={STYLE_ERROR}>{errorInputMsg}</span>}
    </div>
  </div>
);

const ClientDepositsIntro = ({ deposits }) => {
  const { t } = useTranslation();
  const { available, locked, used } = calculateSums(deposits);

  return (
    <div data-bem="ClientsDepositTable__head" className="thead">
      <div className="cell-name">{t('checkout.clientsDepositTable.columnHeaders.clientDeposits')}</div>

      <div className="cell-balance text-r">
        {t('checkout.clientsDepositTable.columnHeaders.availableDeposits')} <CurrencySign value={available} />
      </div>

      <div className="cell-use text-r">
        {t('checkout.clientsDepositTable.columnHeaders.otherDeposits')} <CurrencySign value={locked} />
      </div>

      <div className="cell-used text-r">
        {t('checkout.clientsDepositTable.columnHeaders.redeemed')} <CurrencySign value={used} />
      </div>

      <div className="cell-empty" />
    </div>
  );
};

class ClientsDepositTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      depositChange: null,
      errorInputMsg: '',
      errorFe_id: 0,
      activeDepositId: 0,
      inputTyping: false,
      countAppointment: 1,
      modal: false,
      depositClicked: 0,
      depositListData: null,
      searchDepositInitiated: false,
    };
  }

  // noinspection JSCheckFunctionSignatures
  UNSAFE_componentWillReceiveProps(nextProps) {
    const FTAG = 'UNSAFE_componentWillReceiveProps()';

    const prevProps = this.props;

    const { countAppointment: stateApCount, searchDepositInitiated, depositChange } = this.state;
    const { clientId, depositUnlock, isLoaded } = nextProps;
    const depositsFromState = asArray(this.state.depositListData);

    if (nextProps.depositListData) {
      const depositListData = fromAnyToJsonClone(nextProps.depositListData);
      const depositsCount = depositListData.length;

      for (let index = 0; index < depositsCount; index += 1) {
        const deposit = depositListData[index];

        const isSameClientId = fromAnyToBool(deposit?.clientId === clientId);
        const isBeforeSearch = !isSameClientId && !searchDepositInitiated;
        const isAfterSearch = isSameClientId && !isBeforeSearch && !depositChange;

        // deposit added by search default to not selected
        if (isBeforeSearch) {
          deposit.selected = isSameClientId;
          deposit.use_deposit = 0;
          deposit.depositType = 'deposited';
          this.setState({ searchDepositInitiated: true });
        }

        if (isAfterSearch) {
          this.setState({
            depositChange: replaceAt({
              index,
              what: fromAnyToString(deposit?.total),
              array: createFullArray({ size: depositsCount, item: S.zero }),
            }),
          });
        }
      } // for

      this.setState({ depositListData });
    }

    const nextClientsCount = alen(nextProps.clients);
    const prevClientsCount = alen(prevProps.clients);

    const nextApCount = alen(nextProps.appointments);
    const prevApCount = alen(prevProps.appointments);

    const hasAppointments = isFullArray(nextProps.appointments);

    const hasClients = nextClientsCount > 0;
    const hasSameNumberOfClients = prevClientsCount === nextClientsCount;
    const hasNoNewAppointments = stateApCount >= nextApCount;

    if (clientId && (hasAppointments || hasClients) && !(isLoaded && hasNoNewAppointments && hasSameNumberOfClients)) {
      this.setState({ countAppointment: prevApCount });
    }

    L.debug(FTAG, 'depositUnlock:', depositUnlock);

    if (USE_NEW_UNTESTED_DEPOSIT_UNLOCK && depositUnlock) {
      const unlockedIds = Object.entries(depositUnlock)
        .filter(([, val]) => val === 'Success')
        .map(([key]) => key);

      if (alen(unlockedIds) && alen(depositsFromState)) {
        for (const currentDeposit of depositsFromState) {
          if (!alen(currentDeposit?.locked_on_appointments)) continue;

          const lockedIds = asArray(currentDeposit?.locked_on_appointments)
            .filter(($) => $.locked)
            .map(($) => fromAnyToString($?.appointment));

          L.debug(FTAG, { currentDeposit, depositsFromState, lockedIds, unlockedIds });

          for (let index = 0; index < alen(lockedIds); index += 1) {
            for (const unlockedOne of unlockedIds) {
              if (lockedIds.includes(unlockedOne)) {
                const j1 = J(currentDeposit.locked_on_appointments);
                L.debug(FTAG, 'unlockedOne:', unlockedOne, 'in lockedIds:', lockedIds, 'removing at index:', index, j1);

                currentDeposit.locked_on_appointments.splice(index, 1);

                const j2 = J(currentDeposit.locked_on_appointments);
                L.debug('DONE removing, the result is:', j2);
              }
            }
          }
        }
      }

      // NOTE: a new array makes the component re-render
      this.setState({ depositListData: [...depositsFromState] });
    } // if USE_NEW_DEPOSIT_UNLOCK && depositUnlock
    else if (depositUnlock) {
      const unlockedIds = Object.entries(depositUnlock)
        .filter(([, val]) => val === 'Success')
        .map(([key]) => key);

      const depositsCount = depositsFromState.length;
      const unlockedCount = unlockedIds.length;

      if (unlockedCount && depositsCount) {
        for (const currentDeposit of depositsFromState) {
          if (!currentDeposit?.locked_on_appointments?.length) continue;

          const lockedIds = Object.keys(currentDeposit?.locked_on_appointments);

          for (let lockedOneIndex = 0; lockedOneIndex < lockedIds.length; lockedOneIndex += 1) {
            const lockedOne = lockedIds[lockedOneIndex];

            for (const unlockedOne of unlockedIds) {
              if (lockedOne.indexOf(unlockedOne) >= 0) {
                currentDeposit.locked_on_appointments.splice(lockedOneIndex, 1);
              }
            }
          }
        }
      }

      // NOTE: a new array makes the component re-render
      this.setState({ depositListData: [...depositsFromState] });
    } // else-if depositUnlock
  }

  onCheck = (ev) => {
    const { deposit, index } = ev ?? {};
    const { setCheckoutPayButtons, total, updateDepositFromCheckout } = this.props;
    const { depositListData: depositListDataFromState, depositChange } = this.state;

    let errorInputMsg = '';

    for (const item of asArray(depositListDataFromState).filter(identity)) {
      const { available, fe_Id: errorFeId, use_deposit: oldDeposit } = item;

      if (deposit.fe_Id !== errorFeId) continue;
      if (USE_OLD_USE_DEPOSIT && deposit.selected) item.old_use_deposit = oldDeposit;

      const isSelected = !item.selected;
      const newDeposit = isSelected ? oldDeposit || available : 0;
      const isNewDepositHigher = total < newDeposit;

      if (isSelected === false) {
        errorInputMsg = '';
      } else if (isNewDepositHigher) {
        errorInputMsg = this.props.t('checkout.clientsDepositTable.errorInputMsg1');
      }

      setCheckoutPayButtons(isNewDepositHigher);

      if (isSelected === false) {
        if (depositChange) {
          const newDepositChange = [...depositChange];
          newDepositChange[index] = S.zero;
          this.setState({ depositChange: newDepositChange });
        }

        // if everything adds up, enable the pay button (with setting disable to false)
        // TODO: figure out how to remove the double negative: setting disable to true or enable to false,
        if (total < 0 ? total + oldDeposit : total - oldDeposit >= 0) {
          setCheckoutPayButtons(false);
        }
      }

      item.selected = isSelected;
      item.use_deposit = newDeposit;

      updateDepositFromCheckout(item);

      this.setState({ inputTyping: false, errorInputMsg, errorFe_id: errorFeId });
    }
  };

  onChange = (ev) => {
    if (ev?.is?.nan) return;

    const { setCheckoutPayButtons, total, updateDepositFromCheckout } = this.props;
    const { depositChange: depositChangeFromState, depositListData } = this.state;

    for (const item of depositListData) {
      if (item.fe_Id !== ev?.feId) continue;

      const parsed = parseMoney({ value: item.use_deposit });

      const isTooHighDeposit = ev?.number > (parsed.is.positive ? total + parsed.number : total);
      const isMoreThanAvailable = ev?.number > item.available;

      // eslint-disable-next-line no-nested-ternary
      const errorInputMsg = isTooHighDeposit ? this.props.t('checkout.clientsDepositTable.errorInputMsg2') : isMoreThanAvailable ? this.props.t('checkout.clientsDepositTable.errorInputMsg3') : '';

      setCheckoutPayButtons(isTooHighDeposit || isMoreThanAvailable);

      item.use_deposit = ev?.number;
      item.selected = !ev?.is?.empty;

      this.setState({ errorInputMsg, errorFe_id: item.fe_Id });

      updateDepositFromCheckout(item);
      return item;
    }

    const depositChange = replaceAt({ array: depositChangeFromState, index: ev?.index, what: ev?.raw });
    this.setState({ depositChange });
  };

  onBlur = () => void this.setState({ inputTyping: false });
  openModal = (depositClicked) => void this.setState({ modal: true, depositClicked });

  closeModal = () => {
    const { fetchAllDepositClientsList, salonId, fetchClientDepositList, clients, appointments, clientId } = this.props;

    this.setState({ modal: false });

    const appointmentIds = fromArrayToCommatose(appointments);

    for (const client of asArray(clients)) {
      const cId = fromAnyToNumber(client?.id || clientId) || 0;
      L.unignore('closeModal()', 'fetchClientDepositList:', fetchClientDepositList(salonId, cId, appointmentIds));
    }

    L.unignore('closeModal()', 'fetchAllDepositClientsList:', fetchAllDepositClientsList(salonId));
  };

  render() {
    const {
      appointmentStartTimeList,
      isFetching,
      depositUnlock,
      unlockDeposit,
      fetchClientDepositDetails,
      getAppointmentStartTimeList,
      clientDepositData,
      searchDepositAdded,
      salonId,
      depositClientListSuccess,
      isLoading,
      depositListData: depositsFromProps,
      t,
    } = this.props;

    const { errorFe_id: errorFeId, inputTyping, depositClicked, depositChange, modal, errorInputMsg, activeDepositId, depositListData: depositsFromState } = this.state;

    const deposits = asArray(depositsFromState);
    const hasNoDeposits = isFalsy(deposits.length > 0);

    const pDepositsCount = alen(depositsFromProps);
    const sDepositsCount = alen(depositsFromState);

    if (0 !== pDepositsCount || 0 !== sDepositsCount) {
      L.debug('render()', pDepositsCount, sDepositsCount, '|', depositsFromProps, '->', depositsFromState);
    }

    return (
      <>
        <section data-bem="ClientsDepositTable" className="scrollable-in-tablet no-margin">
          <div className="table-checkout tbl-client-to-use-deposit">
            <ClientDepositsIntro deposits={deposits} />

            <div data-bem="ClientsDepositTable__body" className="tbody">
              <div data-bem="ClientsDepositTable__no-deposit" className="tr" style={STYLE_NO_PADDING}>
                {hasNoDeposits && (
                  <div>
                    <span className="no-deposit-message">{t('checkout.clientsDepositTable.noDepositsMsg')}</span>
                  </div>
                )}
              </div>

              {isFetching ? (
                <LoadingIndicator />
              ) : (
                deposits.map((item, index) => {
                  const { client_name: clientName, use_deposit: deposit, fe_Id: feId, source, locked, selected, available } = item ?? {};

                  const key = createNodeKey({ unique: feId, index });
                  const htmlFor = `clints-check-${feId}`;

                  const change = asArray(depositChange)[index];
                  const c = parseMoney({ value: change });
                  const d = parseMoney({ value: deposit });

                  const redeemedValue = inputTyping && activeDepositId === feId && c.is.valid ? c.string : d.string;

                  const handleView = () => this.openModal(index);
                  const handleCheck = () => void this.onCheck({ deposit: item, index });
                  const handleChange = (ev) => void this.onChange({ ...ev, feId, index });
                  const handleBlur = () => void this.onBlur({ item });

                  const handleClick = ({ ev }) => {
                    const { depositChange: depositChangeFromState } = this.state;

                    moveCursorAtEnd(ev.target);

                    const depositChange = depositChangeFromState
                      ? replaceAt({
                          index,
                          array: depositChangeFromState,
                          what: deposit && deposit > 0 ? fromAnyToString(deposit) : '',
                        })
                      : depositChangeFromState;

                    this.setState({ inputTyping: true, activeDepositId: feId, depositChange });
                  };

                  return (
                    <ClientDepositLine
                      key={key}
                      tag={ct(key, index, feId)}
                      id={htmlFor}
                      checked={selected}
                      onChange={handleCheck}
                      available={available}
                      clientName={clientName}
                      source={source}
                      locked={locked}
                      redeemed={redeemedValue}
                      onViewDetails={handleView}
                      onMoneyClick={handleClick}
                      onMoneyChange={handleChange}
                      onMoneyBlur={handleBlur}
                      errorFeId={errorFeId}
                      feId={feId}
                      errorInputMsg={errorInputMsg}
                      t={t}
                    />
                  );
                })
              )}
              {USE_LOADER && <LoadingIndicator />}
              {fromAnyToBool(isLoading && !depositClientListSuccess) && <LoadingIndicator />}
            </div>
          </div>
        </section>

        {modal && (
          <UnlockedDeposit
            salonId={salonId}
            getAppointmentStartTimeList={getAppointmentStartTimeList}
            fetchClientDepositDetails={fetchClientDepositDetails}
            unlockDeposit={unlockDeposit}
            onClose={this.closeModal}
            hideBtnDepositTable={noop}
            items={depositsFromState[depositClicked]}
            clientDepositData={clientDepositData}
            appointmentStartTimeList={appointmentStartTimeList}
            depositUnlock={depositUnlock}
            searchDepositAdded={searchDepositAdded}
            t={t}
          />
        )}
      </>
    );
  }
}

// this extra export is needed for tests that provide their own mock for connect()

const ClientsDepositTableHoc = withTranslation()(ClientsDepositTable);

export { ClientsDepositTableHoc as ClientsDepositTable4Test };

export default ClientsDepositTableHoc;
